import { CameraAnimationProps } from "@/components/r3f/animations/camera-animation";
import {
  useCameraPredefinedViews,
  useResetPredefinedViewWhenCameraMoves,
} from "@/hooks/use-camera-predefined-views";
import { CameraView, CameraViewType } from "@/utils/cam-view";
import { OrientedBoundingBox, WalkOrbitControls } from "@faro-lotv/lotv";
import { useThree } from "@react-three/fiber";
import { useCallback, useState } from "react";
import { Box3, Vector3 } from "three";

/**
 * Provides logic to animate the camera when the user switches the camera predefined views.
 *
 * @param modelBB The model's bounding box
 * @param onTargetChanged A function called when the camera target changes
 * @returns The camera animation properties and a function to set the controls to the hook
 */
export function useAnimateCameraToPredefinedViews(
  modelBB: Box3,
  onTargetChanged: (target: Vector3 | undefined) => void,
): AnimationReturnType {
  const [cameraAnimation, setCameraAnimation] = useState<
    CameraAnimationProps | undefined
  >();

  const aspectRatio = useThree((s) => s.size.width / s.size.height);

  const setCameraView = useCallback(
    (cameraView: CameraView) => {
      const isOrtho = cameraView.type === CameraViewType.orthographic;
      onTargetChanged(cameraView.target);
      setCameraAnimation({
        position: cameraView.position,
        quaternion: cameraView.rotation,
        fov: isOrtho ? undefined : cameraView.zoomLevel,
        frustum:
          isOrtho && !!cameraView.target
            ? {
                top: cameraView.zoomLevel * 0.5,
                right: cameraView.zoomLevel * 0.5 * aspectRatio,
                bottom: -cameraView.zoomLevel * 0.5,
                left: -cameraView.zoomLevel * 0.5 * aspectRatio,
                bboxCenter: cameraView.target,
              }
            : undefined,
      });
    },
    [aspectRatio, onTargetChanged],
  );

  const clippingBox = useCameraPredefinedViews(
    setCameraView,
    undefined,
    modelBB,
  );
  const setControlsForResetView = useResetPredefinedViewWhenCameraMoves();

  return {
    cameraAnimation,
    setCameraAnimation,
    setControlsForResetView,
    clippingBox,
    setCameraView,
  };
}

type AnimationReturnType = {
  /** Camera animation, or undefined if no animation is running */
  cameraAnimation: CameraAnimationProps | undefined;

  /** Camera animation setter */
  setCameraAnimation(cameraAnimation: CameraAnimationProps | undefined): void;

  /** Sets the WalkOrbitControls, to get notified when the camera exits the predefined views */
  setControlsForResetView(controls: WalkOrbitControls | null): void;

  /** The clipping box the predefined views are centered on */
  clippingBox: OrientedBoundingBox | undefined;

  /** Function to start a camera animation to bring the camera to the given view */
  setCameraView(cameraView: CameraView): void;
};
